import {
  Box,
  Button,
  ButtonGroup,
  Fab,
  Grid,
  Pagination,
  styled,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import LayoutBase from '../../shared/layouts';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { api } from '../../shared/services/api';
import Cookies from 'js-cookie';
import UndrawNoData from '../../assets/svg/undraw_no_data.svg';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import CardCcs0003 from './components/cardCcs0003';

import {
  IResponseCCS0003,
  IResponseDepositAcceptance,
} from '../../shared/services/types/response';
import ModalDetail from './components/modalDetails';
import InputSearch from './components/inputSearch';
import { deleteAccount } from './components/modalDetails/service';
import ConfirmDeleteModal from './components/confirmDeleteModal';
import Loading from '../../components/loading';
import CardDepositAcceptance from './components/cardDepositAcceptance';
import {
  acceptDeposit,
  refuseDeposit,
  getDepositsAwaitingAcceptance,
} from './components/cardDepositAcceptance/service';
import ModalDepositAcceptance from './components/modalDepositAcceptance';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CCS0003 = () => {
  const token = Cookies.get('@token');
  const [activeTab, setActiveTab] = useState<number>(0);

  // States CCS0003
  const [ccs0003Data, setCcs0003Data] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IResponseCCS0003 | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(0);
  const [filter, setFilter] = useState<string>('ALL');
  const [number, setNumber] = useState<string>('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // States Aceite de Depósitos
  const [depositsAwaitingAcceptance, setDepositsAwaitingAcceptance] = useState<IResponseDepositAcceptance[]>(
    [],
  );
  const [isLoadingAcceptance, setIsLoadingAcceptance] = useState<boolean>(false);
  const [currentPageAcceptance, setCurrentPageAcceptance] = useState<number>(1);
  const [pagesAcceptance, setPagesAcceptance] = useState<number>(0);
  const [selectedDepositAcceptance, setSelectedDepositAcceptance] =
    useState<IResponseDepositAcceptance | null>(null);
  const [isProcessingAcceptance, setIsProcessingAcceptance] =
    useState<boolean>(false);
  const [modalAcceptanceOpen, setModalAcceptanceOpen] = useState(false);

  const itemsPerPage = 8;

  // Atualiza a página ao mudar o filtro ou a página (CCS0003)
  useEffect(() => {
    if (activeTab === 0) {
      handleGetAll();
    }
  }, [currentPage, filter, number, activeTab]);

  // Carrega depósitos aguardando aceite quando tab muda
  useEffect(() => {
    if (activeTab === 1) {
      handleGetDepositsAwaitingAcceptance();
    }
  }, [activeTab, currentPageAcceptance]);

  // Recarrega depósitos quando modal fecha (após aceitar/recusar)
  useEffect(() => {
    if (!modalAcceptanceOpen && activeTab === 1) {
      handleGetDepositsAwaitingAcceptance();
    }
  }, [modalAcceptanceOpen, activeTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // ============ CCS0003 HANDLERS ============

  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleFilterNumberChange = (newFilter: string) => {
    setNumber(newFilter);
    setCurrentPage(1);
  };

  const handleClearFilterNumberChange = () => {
    setNumber('');
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);

    const result = await deleteAccount(deleteId);

    if (result.success) {
      toast.success('Arquivo excluído com sucesso');
      setDeleteModalOpen(false);
      setDeleteId(null);
      handleGetAll();
      setDeleteLoading(false);
    } else {
      toast.error(result.message || 'Erro ao excluir arquivo');
      setDeleteLoading(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleGetAll = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        limit: itemsPerPage,
        page: currentPage,
        status: filter !== 'ALL' ? filter : undefined,
        ...(number !== '' && { number_control_ccs: number }),
      },
    };

    try {
      setIsLoading(true);
      const responseDetailPerson = await api.get('/deposit/getAll', config);
      const {
        data,
        meta: { totalPages },
      } = responseDetailPerson.data;

      setCcs0003Data(data || []);
      setPages(totalPages || 1);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadCcsArchive = async (files: FileList | null) => {
    setIsUploadLoading(true);

    if (!files || files?.length === 0) {
      setIsUploadLoading(false);
      return;
    }

    if (files[0].type != 'text/xml') {
      toast.warn('Tipo de arquivo inválido');
      setIsUploadLoading(false);
      return;
    }

    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    };

    const formDataBody = new FormData();
    const ccsArchive = files?.[0];

    formDataBody.append('file', ccsArchive);
    try {
      const responseUploadDepositAcc = await api.post(
        '/archive/upload-deposit-account',
        formDataBody,
        config,
      );

      if (responseUploadDepositAcc.status === 201) {
        toast.success('Upload feito com sucesso');
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      handleGetAll();
      setIsUploadLoading(false);
    }
  };

  const handleOpenModal = (item: IResponseCCS0003) => {
    setSelectedItem(item);
  };

  // ============ ACEITE DE DEPÓSITOS HANDLERS ============

  const handleGetDepositsAwaitingAcceptance = async () => {
    setIsLoadingAcceptance(true);
    const result = await getDepositsAwaitingAcceptance(
      currentPageAcceptance,
      itemsPerPage,
    );

    if (result.success) {
      setDepositsAwaitingAcceptance(result.data || []);
      setPagesAcceptance(result.totalPages || 1);
    } else {
      toast.error(result.message || 'Erro ao buscar depósitos');
    }

    setIsLoadingAcceptance(false);
  };

  const handleAcceptDeposit = (deposit: IResponseDepositAcceptance) => {
    setSelectedDepositAcceptance(deposit);
    setModalAcceptanceOpen(true);
  };

  const handleRefuseDeposit = (deposit: IResponseDepositAcceptance) => {
    setSelectedDepositAcceptance(deposit);
    setModalAcceptanceOpen(true);
  };

  const handleConfirmAccept = async () => {
    if (!selectedDepositAcceptance) return;

    setIsProcessingAcceptance(true);
    const result = await acceptDeposit(
      selectedDepositAcceptance.id,
    );

    if (result.success) {
      toast.success('Depósito aceito com sucesso');
      setModalAcceptanceOpen(false);
      setSelectedDepositAcceptance(null);
      // Reseta para página 1 (useEffect fará o recarregamento automático)
      setCurrentPageAcceptance(1);
    } else {
      toast.error(result.message || 'Erro ao aceitar depósito');
    }

    setIsProcessingAcceptance(false);
  };

  const handleConfirmRefuse = async (reason?: string) => {
    if (!selectedDepositAcceptance) return;

    setIsProcessingAcceptance(true);
    const result = await refuseDeposit(
      selectedDepositAcceptance.id,
      reason,
    );

    if (result.success) {
      toast.success('Depósito recusado com sucesso');
      setModalAcceptanceOpen(false);
      setSelectedDepositAcceptance(null);
      // Reseta para página 1 (useEffect fará o recarregamento automático)
      setCurrentPageAcceptance(1);
    } else {
      toast.error(result.message || 'Erro ao recusar depósito');
    }

    setIsProcessingAcceptance(false);
  };

  const handleChangePageAcceptance = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPageAcceptance(value);
  };

  return (
    <LayoutBase
      footer={
        activeTab === 0
          ? isLoading || ccs0003Data.length === 0
            ? null
            : (
              <Pagination
                count={pages}
                page={currentPage}
                onChange={handleChange}
                color="primary"
              />
            )
          : isLoadingAcceptance || depositsAwaitingAcceptance.length === 0
            ? null
            : (
              <Pagination
                count={pagesAcceptance}
                page={currentPageAcceptance}
                onChange={handleChangePageAcceptance}
                color="primary"
              />
            )
      }
    >
      <Typography color="textPrimary" variant="h5">
        CCS0003 e Aceite de Depósitos
      </Typography>
      <Typography sx={{ marginBottom: 2 }}>
        Gerencie arquivos CCS0003 e realize o aceite de depósitos.
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ marginBottom: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="CCS0003" />
        <Tab label={`Aceite de Depósitos (${depositsAwaitingAcceptance.length})`} />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Grid
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Fab
                component="label"
                size="medium"
                color="secondary"
                aria-label="add"
                disabled={isUploadLoading}
              >
                <AddIcon />
                <VisuallyHiddenInput
                  type="file"
                  onChange={(event) => handleUploadCcsArchive(event.target.files)}
                  multiple
                />
              </Fab>

              <ButtonGroup
                variant="outlined"
                color="primary"
              >
                {Object.entries(filtersObject).map(([label, value]) => (
                  <Button
                    key={value}
                    variant={filter === value ? 'contained' : 'outlined'}
                    onClick={() => handleFilterChange(value)}
                  >
                    {label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
            <InputSearch
              label="Pesquise pelo número de controle"
              onSearch={handleFilterNumberChange}
              handleClearInput={handleClearFilterNumberChange}
            />
          </Grid>
          <Loading isLoading={isLoading} />
          {ccs0003Data.length === 0 && !isLoading ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box
                  component="img"
                  src={UndrawNoData}
                  alt="Svg em construção"
                  sx={{
                    maxWidth: '15%',
                    height: 'auto',
                    marginTop: 10,
                  }}
                />
              </Box>
              <Typography textAlign={'center'} sx={{ marginTop: 2 }}>
                Dados não encontrados...
              </Typography>
            </>
          ) : (
            <Box sx={{ padding: 3 }}>
              <Grid
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                  gap: 2,
                }}
              >
                {ccs0003Data.map((item: IResponseCCS0003) => (
                  <CardCcs0003
                    key={item.id}
                    ccsValue={item}
                    handleOpenModal={() => handleOpenModal(item)}
                    handleDeleteClick={() => handleDeleteClick(item.id)}
                  />
                ))}
              </Grid>
              <ModalDetail
                open={selectedItem !== null}
                ccsId={selectedItem}
                closeModal={(shouldReload?: boolean) => {
                  setSelectedItem(null);
                  if (shouldReload) handleGetAll();
                }}
                reloadData={handleGetAll}
              />

              <ConfirmDeleteModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDelete}
                itemName="este arquivo"
                isloading={deleteLoading}
              />
            </Box>
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          <Loading isLoading={isLoadingAcceptance} />
          {depositsAwaitingAcceptance.length === 0 && !isLoadingAcceptance ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box
                  component="img"
                  src={UndrawNoData}
                  alt="Svg em construção"
                  sx={{
                    maxWidth: '15%',
                    height: 'auto',
                    marginTop: 10,
                  }}
                />
              </Box>
              <Typography textAlign={'center'} sx={{ marginTop: 2 }}>
                Nenhum depósito aguardando aceite...
              </Typography>
            </>
          ) : (
            <Box sx={{ padding: 3 }}>
              <Grid
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                  gap: 2,
                }}
              >
                {depositsAwaitingAcceptance.map(
                  (deposit: IResponseDepositAcceptance) => (
                    <CardDepositAcceptance
                      key={deposit.id}
                      depositValue={deposit}
                      onAccept={() => handleAcceptDeposit(deposit)}
                      onRefuse={() => handleRefuseDeposit(deposit)}
                      isLoading={isProcessingAcceptance}
                    />
                  ),
                )}
              </Grid>

              <ModalDepositAcceptance
                open={modalAcceptanceOpen}
                deposit={selectedDepositAcceptance}
                onClose={() => {
                  setModalAcceptanceOpen(false);
                  setSelectedDepositAcceptance(null);
                }}
                onAccept={handleConfirmAccept}
                onRefuse={handleConfirmRefuse}
                isLoading={isProcessingAcceptance}
              />
            </Box>
          )}
        </>
      )}
    </LayoutBase>
  );
};

export default CCS0003;

const filtersObject = {
  Todos: 'ALL',
  Criado: 'CREATED',
  Pendente: 'PENDING',
  Deferido: 'DONE',
  Cancelado: 'CANCEL',
  Recusado: 'REFUSED',
  Aguardando: 'AWAITING_MANUAL_ACCEPTANCE',
};
